<template>
  <div class="timesheet-frequency-list-container timesheet-frequency-list-main timesheet-frequency-list-page--glass glass-tab-page">
    <BrucdcumHeader :breadcrumb="['Company', 'Timesheet Frequency List']" />

    <div class="glass-tab-view glass-tab-view--flush timesheet-frequency-list-glass-shell">
      <div class="glass-tab-view__content timesheet-frequency-list-tab-content timesheet-frequency-list-page-stack">
        <div class="timesheet-frequency-list-header-block">
          <div class="timesheet-frequency-list-header-row">
            <div class="timesheet-frequency-list-header-row__text">
              <h2>Timesheet Frequencies</h2>
              <p class="timesheet-frequency-list-header-row__sub">Create and manage timesheet billing frequency options</p>
            </div>
            <div class="timesheet-frequency-list-header-actions">
              <SecondaryButton
                v-if="showLoadDefaultsButton"
                label="Load defaults"
                icon="bi bi-download"
                :loading="loadingDefaults"
                :disabled="loading"
                :title="defaultNamesTitle"
                @click="loadDefaults"
              />
              <PrimaryButton label="Add New" @click="showModal = true" />
            </div>
          </div>
        </div>

        <div class="timesheet-frequency-list-glass-panel timesheet-frequency-list-main-card">
          <div class="consultant-glass-table-section timesheet-frequency-list-table-section">
            <div
              class="consultant-glass-table-responsive consultant-glass-table-responsive--framed"
              :class="{ 'consultant-glass-table-responsive--loading': loading }"
            >
              <div class="consultant-glass-table-scroll-x">
                <table class="consultant-glass-table timesheet-frequency-list-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th class="timesheet-frequency-list-delete-header">Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="loading">
                      <td colspan="2" class="consultant-glass-table-no-records timesheet-frequency-list-no-records">
                        Loading...
                      </td>
                    </tr>
                    <tr v-else-if="error">
                      <td colspan="2" class="consultant-glass-table-no-records timesheet-frequency-list-no-records">{{ error }}</td>
                    </tr>
                    <tr v-else-if="frequencies.length === 0">
                      <td colspan="2" class="consultant-glass-table-no-records timesheet-frequency-list-no-records">
                        No records to show
                      </td>
                    </tr>
                    <template v-else>
                      <tr
                        v-for="(frequency, idx) in frequencies"
                        :key="frequency.id || idx"
                        class="consultant-glass-table-row timesheet-frequency-list-row"
                        :style="{ '--tfl-row-delay': `${0.04 + Math.min(idx, 12) * 0.03}s` }"
                      >
                        <td>{{ frequency.name }}</td>
                        <td class="timesheet-frequency-list-delete-cell">
                          <SecondaryButton
                            label="Delete"
                            icon="bi bi-trash"
                            custom-class="consultant-secondary-btn--danger timesheet-frequency-list-delete-btn"
                            @click="openDeleteModal(idx)"
                          />
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <AddNewTimesheetFrequency
      :visible="showModal"
      :companyId="companyId"
      @ok="handleTimesheetFrequencyCreated"
      @cancel="closeModal"
    />

    <SuccessModal
      :show="showSuccessModal"
      title="Success"
      :message="successMessage"
      buttonText="OK"
      @close="closeSuccessModal"
    />

    <ErrorModal
      :show="showErrorModal"
      title="Error"
      :message="errorMessage"
      buttonText="OK"
      @close="closeErrorModal"
    />

    <WarningModal
      :show="showWarningModal"
      title="Delete Timesheet Frequency"
      :message="warningMessage"
      buttonText="Cancel"
      confirmButtonText="Delete"
      @close="closeWarningModal"
      @confirm="confirmDeleteFrequency"
    />
  </div>
</template>

<script>
import '../styles/TimesheetFrequencyList.css';
import AddNewTimesheetFrequency from './AddNewTimesheetFrequency.vue';
import logger from '@/utils/logger';
import CompanyService from '@/services/company.service.js';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import SuccessModal from '@/components/SuccessModal.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import WarningModal from '@/components/WarningModal.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

/** Keep in sync with backend DEFAULT_TIMESHEET_FREQUENCY_NAMES (companyController). */
const DEFAULT_TIMESHEET_FREQUENCY_NAMES = [
  'Weekly',
  'Bi-Weekly',
  'Semi-Monthly',
  'Monthly',
  'Quarterly',
];

export default {
  name: 'TimesheetFrequencyList',
  components: {
    AddNewTimesheetFrequency,
    BrucdcumHeader,
    SuccessModal,
    ErrorModal,
    WarningModal,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    return {
      showModal: false,
      frequencies: [],
      loading: false,
      error: '',
      companyId: this.$route.params.id,
      showSuccessModal: false,
      showErrorModal: false,
      showWarningModal: false,
      errorMessage: '',
      warningMessage: '',
      successMessage: '',
      deleteIdx: null,
      isDeleting: false,
      loadingDefaults: false,
    };
  },
  computed: {
    missingDefaultTimesheetFrequencyNames() {
      const existing = new Set((this.frequencies || []).map((f) => f.name));
      return DEFAULT_TIMESHEET_FREQUENCY_NAMES.filter((n) => !existing.has(n));
    },
    showLoadDefaultsButton() {
      if (this.loading) return false;
      return this.missingDefaultTimesheetFrequencyNames.length > 0;
    },
    defaultNamesTitle() {
      const missing = this.missingDefaultTimesheetFrequencyNames;
      if (!missing.length) return '';
      return `Adds missing: ${missing.join(', ')}`;
    },
  },
  async mounted() {
    await this.fetchFrequencies();
  },
  methods: {
    async fetchFrequencies() {
      this.loading = true;
      this.error = '';
      try {
        this.frequencies = await CompanyService.getAllTimesheetFrequencies(this.companyId);
        logger.log('Successfully fetched timesheet frequencies:', this.frequencies.length);
      } catch (err) {
        logger.error('Error fetching timesheet frequencies:', err);
        this.errorMessage =
          err.response?.data?.message ||
          err.message ||
          'Failed to load timesheet frequencies.';
        this.showErrorModal = true;
      } finally {
        this.loading = false;
      }
    },
    closeModal() {
      this.showModal = false;
    },
    async loadDefaults() {
      this.loadingDefaults = true;
      try {
        const data = await CompanyService.loadDefaultTimesheetFrequencies(this.companyId);
        this.frequencies = data.frequencyList || [];
        const created = data.created ?? 0;
        if (created > 0) {
          this.successMessage =
            data.message ||
            `Added ${created} default timesheet frequenc${created === 1 ? 'y' : 'ies'}.`;
          this.showSuccessModal = true;
        }
      } catch (err) {
        this.errorMessage =
          err.response?.data?.message || err.message || 'Failed to load default timesheet frequencies.';
        this.showErrorModal = true;
      } finally {
        this.loadingDefaults = false;
      }
    },
    async handleTimesheetFrequencyCreated(createdFrequency) {
      this.closeModal();
      await this.fetchFrequencies();
      const frequencyCount = this.frequencies.length;
      this.successMessage = `Timesheet frequency "${createdFrequency?.name || 'New frequency'}" created successfully. You now have ${frequencyCount} timesheet frequenc${frequencyCount !== 1 ? 'ies' : 'y'}.`;
      this.showSuccessModal = true;
    },
    openDeleteModal(idx) {
      const frequency = this.frequencies[idx];
      if (!frequency || !frequency.id) {
        this.errorMessage = 'Invalid frequency selected. Please refresh the page and try again.';
        this.showErrorModal = true;
        return;
      }
      if (!frequency.name || frequency.name.trim() === '') {
        this.errorMessage = 'Cannot delete a frequency with an empty name.';
        this.showErrorModal = true;
        return;
      }
      this.deleteIdx = idx;
      this.warningMessage = `Are you sure you want to delete the timesheet frequency "${frequency.name}"? This cannot be undone and may affect records that use this frequency.`;
      this.showWarningModal = true;
    },
    closeWarningModal() {
      this.showWarningModal = false;
      this.deleteIdx = null;
      this.warningMessage = '';
    },
    async confirmDeleteFrequency() {
      if (this.deleteIdx === null) return;
      const frequency = this.frequencies[this.deleteIdx];
      if (!frequency || !frequency.id) return;

      this.isDeleting = true;
      this.showWarningModal = false;

      try {
        await CompanyService.deleteTimesheetFrequency(frequency.id);
        this.frequencies.splice(this.deleteIdx, 1);
        const remainingCount = this.frequencies.length;
        this.successMessage = `Timesheet frequency "${frequency.name}" deleted. ${remainingCount} frequenc${remainingCount !== 1 ? 'ies' : 'y'} remaining.`;
        this.showSuccessModal = true;
      } catch (err) {
        logger.error('Error deleting timesheet frequency:', err);
        const errorText =
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          '';
        if (
          errorText.includes('foreign key') ||
          errorText.includes('violates') ||
          errorText.includes('referenced') ||
          errorText.includes('constraint') ||
          err.response?.status === 409
        ) {
          this.errorMessage = `Cannot delete "${frequency.name}" because it is in use. Reassign related records first.`;
        } else {
          this.errorMessage = errorText || 'Failed to delete timesheet frequency.';
        }
        this.showErrorModal = true;
      } finally {
        this.isDeleting = false;
        this.deleteIdx = null;
        this.warningMessage = '';
      }
    },
    closeSuccessModal() {
      this.showSuccessModal = false;
    },
    closeErrorModal() {
      this.showErrorModal = false;
      this.errorMessage = '';
    },
  },
};
</script>
